import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getErrorMessage } from '@billda/shared-ui';
import SparkMD5 from 'spark-md5';
import { useUploadTrayStore, type UploadItem } from '../stores/uploadTrayStore';
import {
  requestFileUploadUrl,
  uploadFileToBlobWithProgress,
  confirmFileUpload,
} from '../api/filesApi';
import { siteFilesKeys } from './useSiteFiles';
import { storageUsageKeys } from './useStorageUsage';
import { folderContentsKeys } from './useFolderContents';

const MAX_CONCURRENT = 3;

const ACTIVE_STATUSES = new Set(['requesting-url', 'uploading', 'confirming']);

/** Module-level set shared across StrictMode remounts. */
const inFlightIds = new Set<string>();

/**
 * Guard against the same item being processed twice.
 * dispatchUploads can re-enter itself via the synchronous Zustand subscription
 * when processItem updates the store, causing the original for-loop to call
 * processItem again for items the recursive call already dispatched.
 */
const processingIds = new Set<string>();

async function processItem(
  item: UploadItem,
  queryClient: ReturnType<typeof useQueryClient>
): Promise<void> {
  if (processingIds.has(item.localId)) {
    return;
  }
  processingIds.add(item.localId);

  const { updateItem } = useUploadTrayStore.getState();
  const { localId, file, fileName, siteId, folderId } = item;

  try {
    updateItem(localId, { status: 'requesting-url' });
    const contentType = file.type || 'application/octet-stream';
    const buffer = await file.arrayBuffer();
    const contentHash = btoa(SparkMD5.ArrayBuffer.hash(buffer, true));
    const response = await requestFileUploadUrl(siteId, {
      fileName,
      contentType,
      fileSizeBytes: file.size,
      contentHash,
      folderId,
    });

    updateItem(localId, {
      status: 'uploading',
      fileId: response.fileId,
      versionId: response.versionId,
      uploadUrl: response.uploadUrl,
    });

    let lastLoaded = 0;
    let lastTime = Date.now();

    await uploadFileToBlobWithProgress(
      response.uploadUrl,
      file,
      contentType,
      (loaded, total) => {
        const now = Date.now();
        const timeDelta = (now - lastTime) / 1000;
        const bytesDelta = loaded - lastLoaded;

        let speed = 0;
        if (timeDelta > 0.5) {
          speed = bytesDelta / timeDelta;
          lastLoaded = loaded;
          lastTime = now;
        }

        const progress = Math.round((loaded / total) * 100);
        const remaining = total - loaded;
        const eta = speed > 0 ? Math.round(remaining / speed) : 0;

        updateItem(localId, { progress, speed, eta });
      }
    );

    updateItem(localId, { status: 'confirming', progress: 100 });
    await confirmFileUpload(siteId, {
      fileId: response.fileId,
      versionId: response.versionId,
    });

    updateItem(localId, { status: 'completed' });

    queryClient.invalidateQueries({ queryKey: siteFilesKeys.list(siteId) });
    queryClient.invalidateQueries({ queryKey: storageUsageKeys.all });
    queryClient.invalidateQueries({ queryKey: folderContentsKeys.all });
  } catch (error: unknown) {
    // 409 = file name collision
    const is409 =
      error !== null &&
      typeof error === 'object' &&
      'response' in error &&
      (error as { response?: { status?: number } }).response?.status === 409;

    if (is409) {
      const ext = fileName.includes('.') ? '.' + fileName.split('.').pop() : '';
      const base = ext ? fileName.slice(0, -ext.length) : fileName;
      const match = base.match(/^(.*)\((\d+)\)$/);
      const stem = match ? match[1] : base + ' ';
      const num = match ? parseInt(match[2], 10) + 1 : 1;
      const suggestedName = `${stem}(${num})${ext}`;

      updateItem(localId, {
        status: 'collision',
        suggestedName,
      });
    } else {
      updateItem(localId, {
        status: 'failed',
        error: getErrorMessage(error),
      });
    }
  } finally {
    processingIds.delete(localId);
  }
}

function dispatchUploads(queryClient: ReturnType<typeof useQueryClient>) {
  const { items } = useUploadTrayStore.getState();

  // Clean up IDs for items that are no longer active
  for (const id of inFlightIds) {
    const item = items.find(i => i.localId === id);
    if (!item || !ACTIVE_STATUSES.has(item.status)) {
      inFlightIds.delete(id);
    }
  }

  // Fill up to MAX_CONCURRENT
  const queued = items.filter(i => i.status === 'queued');
  for (const item of queued) {
    if (inFlightIds.size >= MAX_CONCURRENT) {
      break;
    }
    inFlightIds.add(item.localId);
    processItem(item, queryClient); // fire-and-forget
  }
}

export function useUploadQueue() {
  const queryClient = useQueryClient();

  useEffect(() => {
    const unsubscribe = useUploadTrayStore.subscribe(() => {
      dispatchUploads(queryClient);
    });

    // Process any items already queued when the hook mounts
    dispatchUploads(queryClient);

    return unsubscribe;
  }, [queryClient]);
}
